/* REVELASHION — sliders .hs : drag souris + points indicateurs.
   Le swipe tactile reste natif (overflow-x + scroll-snap). Un slider
   peut être re-scanné (ex. après un filtre d'onglets UGC) en déclenchant
   un event 'hs:refresh' sur l'élément .hs concerné. */
(() => {
  const state = new WeakMap();

  const buildDots = (hs) => {
    let nav = hs.nextElementSibling && hs.nextElementSibling.classList.contains('hs-nav')
      ? hs.nextElementSibling
      : null;
    if (!nav) {
      nav = document.createElement('div');
      nav.className = 'hs-nav';
      nav.setAttribute('role', 'tablist');
      hs.after(nav);
    }
    nav.innerHTML = '';

    /* Un slider pourvu d'une barre de progression n'affiche pas de points :
       deux indicateurs pour un même geste se contredisent. */
    if (hs.dataset.hsBar) {
      nav.hidden = true;
      return { slides: [], setActive: () => {} };
    }

    const slides = Array.from(hs.children).filter((el) => el.style.display !== 'none');
    if (slides.length < 2) { nav.hidden = true; return { slides: [], setActive: () => {} }; }

    slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', 'Élément ' + (i + 1) + ' sur ' + slides.length);
      dot.addEventListener('click', () => {
        hs.scrollTo({
          left: slide.offsetLeft - (hs.clientWidth - slide.offsetWidth) / 2,
          behavior: 'smooth'
        });
      });
      nav.appendChild(dot);
    });
    const dots = Array.from(nav.querySelectorAll('button'));

    const setActive = () => {
      const center = hs.scrollLeft + hs.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      dots.forEach((dot, i) => dot.classList.toggle('on', i === best));
    };

    nav.hidden = hs.scrollWidth <= hs.clientWidth + 4;
    if (!nav.hidden) setActive();
    return { slides, setActive };
  };

  const bindOnce = (hs) => {
    if (hs.dataset.hsReady) return;
    hs.dataset.hsReady = '1';

    hs.addEventListener('scroll', () => {
      const s = state.get(hs);
      if (s) s.setActive();
    }, { passive: true });

    window.addEventListener('resize', () => state.set(hs, buildDots(hs)));

    /* Déclenché par un filtre d'onglets (ex. ugc-reveals) après avoir
       masqué/affiché des cards : on revient au début et on recompte. */
    hs.addEventListener('hs:refresh', () => {
      hs.scrollLeft = 0;
      state.set(hs, buildDots(hs));
    });

    /* --- Drag à la souris (aperçu desktop / éditeur) --- */
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    hs.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startLeft = hs.scrollLeft;
      /* `is-drag` n'est PAS posée ici. Elle neutralise les liens du
         carrousel, et `pointerdown` précède `mousedown` : le lien
         devenait transparent avant que le navigateur ne choisisse la
         cible du mousedown. Le clic partait alors vers l'ancêtre commun
         du mousedown et du mouseup — jamais vers le lien, qui semblait
         mort. On attend donc qu'un glissement soit avéré. */
    });
    /* Le seuil sépare un clic d'un glissement. À 5 px, le frémissement de
       main qui accompagne un clic ordinaire passait pour un glissement et
       le lien de la carte était annulé : on ne pouvait plus ouvrir une
       fiche produit depuis un carrousel. */
    const SEUIL = 10;
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > SEUIL && !moved) {
        moved = true;
        /* Le glissement est avéré : on peut neutraliser les liens sans
           risquer d'avaler un clic. */
        hs.classList.add('is-drag');
      }
      /* On ne défile qu'une fois le seuil franchi : sous cette distance,
         le geste ne bouge rien et reste un clic. */
      if (moved) hs.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      hs.classList.remove('is-drag');
      /* Le clic est émis juste après le relâchement, donc avant ce
         minuteur : le drapeau sert au clic en cours, puis retombe. Sans
         cela, un glissement non suivi d'un clic laissait le drapeau levé
         et bloquait le clic suivant. */
      setTimeout(() => { moved = false; }, 0);
    });
    /* Un glissement ne doit pas déclencher le lien de la carte relâchée */
    hs.addEventListener('click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  };

  const init = () => {
    document.querySelectorAll('.hs').forEach((hs) => {
      bindOnce(hs);
      state.set(hs, buildDots(hs));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — barre de progression des sliders .hs.

   Un slider remplace ses points par une barre en portant l'attribut
   `data-hs-bar="#id"`, qui désigne l'élément d'interface à piloter :

     <div class="hs" data-hs-bar="#PcarNav-42"> … </div>
     <div class="hsbar" id="PcarNav-42"> … </div>

   Le curseur ne colle pas au défilement au pixel près : il le rattrape.
   Un suivi strict rend le trait nerveux sous le doigt ; un léger retard
   élastique donne au contraire le sentiment d'une matière entraînée. La
   boucle d'animation s'arrête dès que le retard est résorbé — elle ne
   tourne donc que pendant le geste, jamais en fond. */
(() => {
  const RATTRAPAGE = 0.22; /* part du retard reprise à chaque image */
  const ARRET = 0.0004; /* en deçà, le curseur est considéré arrivé */
  const doux = window.matchMedia('(prefers-reduced-motion: reduce)');

  const deuxChiffres = (n) => String(n).padStart(2, '0');

  const setup = (hs) => {
    if (hs.dataset.hsBarReady) return;
    const root = document.querySelector(hs.dataset.hsBar || '');
    if (!root) return;
    hs.dataset.hsBarReady = '1';

    const fill = root.querySelector('.hsbar__fill');
    const rail = root.querySelector('.hsbar__rail');
    const cur = root.querySelector('.hsbar__cur');
    const tot = root.querySelector('.hsbar__tot');
    const prev = root.querySelector('[data-hs-prev]');
    const next = root.querySelector('[data-hs-next]');

    let montre = 0;
    let raf = 0;
    /* Position de chaque vue dans le contenu défilant. Mesurée aux moments
       où elle peut changer, jamais pendant le défilement : `offsetLeft` se
       compte depuis le premier ancêtre positionné, qui n'est pas le rail —
       la comparer à `scrollLeft` fausserait le rang affiché. */
    let bornes = [];

    const vues = () => Array.from(hs.children).filter((el) => el.style.display !== 'none');
    const course = () => hs.scrollWidth - hs.clientWidth;

    const cible = () => {
      const max = course();
      return max > 4 ? Math.min(1, Math.max(0, hs.scrollLeft / max)) : 0;
    };

    /* Le curseur occupe la part visible du rail : sa longueur dit combien
       reste à parcourir, sa position où l'on en est. Un plancher l'empêche
       de disparaître quand le contenu est très long. */
    const dessine = () => {
      const part = hs.scrollWidth > 0 ? hs.clientWidth / hs.scrollWidth : 1;
      const r = Math.min(1, Math.max(0.12, part));
      if (fill) fill.style.transform = `translateX(${montre * (1 - r) * 100}%) scaleX(${r})`;
    };

    const boucle = () => {
      const t = cible();
      const ecart = t - montre;
      montre += Math.abs(ecart) < ARRET || doux.matches ? ecart : ecart * RATTRAPAGE;
      dessine();
      raf = Math.abs(t - montre) > ARRET ? requestAnimationFrame(boucle) : 0;
    };

    const relance = () => {
      if (!raf) raf = requestAnimationFrame(boucle);
    };

    const mesureBornes = () => {
      const base = hs.getBoundingClientRect().left - hs.scrollLeft;
      bornes = vues().map((s) => s.getBoundingClientRect().left - base);
    };

    /* Le rang se lit au bord d'attaque, pas au centre du rail : à l'arrêt
       initial, le centre tombe sur la deuxième carte et le compteur
       annonçait 02 alors que rien n'avait défilé. */
    const actif = () => {
      let best = 0;
      let dist = Infinity;
      bornes.forEach((x, i) => {
        const d = Math.abs(x - hs.scrollLeft);
        if (d < dist) {
          dist = d;
          best = i;
        }
      });
      return best;
    };

    const compte = () => {
      const n = vues().length;
      if (tot) tot.textContent = deuxChiffres(n);

      const rang = deuxChiffres(actif() + 1);
      if (cur && cur.textContent !== rang) {
        cur.textContent = rang;
        /* On relance l'animation du chiffre en la coupant d'abord : sans
           ce reflow, deux changements rapprochés n'en jouent qu'une. */
        cur.classList.remove('is-new');
        void cur.offsetWidth;
        cur.classList.add('is-new');
      }

      const max = course();
      if (prev) prev.disabled = hs.scrollLeft <= 2;
      if (next) next.disabled = hs.scrollLeft >= max - 2;
    };

    const mesure = () => {
      root.hidden = course() <= 4;
      mesureBornes();
      montre = cible();
      dessine();
      compte();
    };

    hs.addEventListener('scroll', () => { relance(); compte(); }, { passive: true });
    window.addEventListener('resize', mesure);
    hs.addEventListener('hs:refresh', mesure);

    /* Sous le doigt, le trait s'épaissit : la barre cesse d'être un témoin
       pour devenir la chose que l'on manipule. `is-drag` est posée par le
       module de glissement ; on l'observe plutôt que de dupliquer sa
       logique de seuil, durement acquise. */
    const suitLeGeste = () => root.classList.toggle('is-live', hs.classList.contains('is-drag'));
    new MutationObserver(suitLeGeste).observe(hs, { attributes: true, attributeFilter: ['class'] });

    /* Un pas = une carte. La mesure se prend sur la carte elle-même : les
       largeurs sont réglables depuis l'admin, une constante mentirait. */
    const pas = (sens) => {
      const s = vues();
      const gap = parseFloat(getComputedStyle(hs).columnGap) || 0;
      const large = s.length ? s[0].getBoundingClientRect().width + gap : hs.clientWidth * 0.8;
      hs.scrollBy({ left: sens * large, behavior: doux.matches ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => pas(-1));
    if (next) next.addEventListener('click', () => pas(1));

    /* Le rail est aussi une piste : on clique où l'on veut aller. Les
       flèches restent la voie clavier, le rail n'est qu'un raccourci. */
    if (rail) {
      rail.addEventListener('click', (e) => {
        const r = rail.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
        hs.scrollTo({ left: p * course(), behavior: doux.matches ? 'auto' : 'smooth' });
      });
    }

    mesure();
  };

  const init = () => document.querySelectorAll('.hs[data-hs-bar]').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — aperçu vidéo au survol des cartes produit.
   La lecture n'est jamais en autoplay : une grille de huit cartes
   chargerait huit vidéos d'un coup. On ne déclenche qu'à l'entrée du
   pointeur, et seulement sur un pointeur fin — sur tactile, « survol »
   n'existe pas et la vidéo resterait figée au premier tap. */
(() => {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!fine.matches) return;

  const bind = (card) => {
    if (card.dataset.hoverVideo) return;
    const video = card.querySelector('.pcard__video');
    if (!video) return;
    card.dataset.hoverVideo = '1';

    card.addEventListener('mouseenter', () => {
      card.classList.add('is-playing');
      const p = video.play();
      /* Un refus de lecture (économie de données, onglet en fond) ne doit
         pas remonter en erreur non gérée : on revient à l'image. */
      if (p && p.catch) p.catch(() => card.classList.remove('is-playing'));
    });

    card.addEventListener('mouseleave', () => {
      card.classList.remove('is-playing');
      video.pause();
      video.currentTime = 0;
    });
  };

  const init = () => document.querySelectorAll('.pcard--video').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — vidéos de vignette, partagées par la mosaïque et les reveals.
   Le comportement est piloté par des attributs, pas par des classes : une
   même mécanique sert deux sections dont l'habillage n'a rien à voir.

   Trois modes, réglés sur la section : lecture quand la vignette entre
   dans le champ, au survol, ou jamais. Dans tous les cas muettes et en
   boucle, les navigateurs refusant l'auto-lecture avec du son.

   Rien n'est préchargé : une grille de six vidéos ne doit pas partir au
   chargement de la page. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const start = (item, video) => {
    item.classList.add('is-playing');
    const p = video.play();
    if (p && p.catch) p.catch(() => item.classList.remove('is-playing'));
  };
  const stop = (item, video) => {
    item.classList.remove('is-playing');
    video.pause();
  };

  let observer = null;
  const viewportObserver = () => {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) start(entry.target, video);
        else stop(entry.target, video);
      });
    }, { threshold: 0.55 });
    return observer;
  };

  const bind = (item) => {
    if (item.dataset.videoReady) return;
    const video = item.querySelector('video');
    if (!video) return;
    item.dataset.videoReady = '1';

    /* Le mouvement réduit vaut consigne : on s'en tient à l'affiche. */
    const mode = reduced ? 'off' : item.dataset.videoPlay || 'viewport';
    if (mode === 'off') return;

    if (mode === 'hover') {
      if (!fine) return;
      item.addEventListener('mouseenter', () => start(item, video));
      item.addEventListener('mouseleave', () => { stop(item, video); video.currentTime = 0; });
      return;
    }

    if ('IntersectionObserver' in window) viewportObserver().observe(item);
    else start(item, video);
  };

  const init = () => document.querySelectorAll('[data-video-item]').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — révélation au défilement.
   Les hooks .rv/data-rv sont posés dans les sections depuis le début ;
   c'est ici qu'ils prennent effet. Un élément n'est révélé qu'une fois :
   rejouer l'animation au retour en arrière donnerait un site qui
   clignote au moindre défilement.

   Les éléments d'une même rangée sont décalés entre eux, pour que la
   grille se compose plutôt qu'elle n'apparaisse d'un bloc. */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const reveal = (el, delay) => {
    if (delay) el.style.setProperty('--rv-delay', delay + 'ms');
    el.classList.add('is-in');
    /* On libère la couche de composition une fois la transition finie. */
    setTimeout(() => el.classList.add('is-done'), 900 + (delay || 0));
  };

  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.rv').forEach((el) => reveal(el, 0));
    return;
  }

  const io = new IntersectionObserver((entries) => {
    /* Les entrées d'un même lot sont ordonnées par position, puis
       décalées : le regard suit la page de haut en bas. */
    const shown = entries.filter((e) => e.isIntersecting);
    shown
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      .forEach((entry, i) => {
        reveal(entry.target, Math.min(i, 5) * 70);
        io.unobserve(entry.target);
      });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const init = () => document.querySelectorAll('.rv:not(.is-in)').forEach((el) => io.observe(el));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — aperçu vidéo sur tactile.
   Le survol n'existe pas au doigt : la vidéo d'une carte produit ne
   partirait jamais. Elle se lance donc d'elle-même après un temps de
   présence à l'écran — assez long pour ne pas parasiter un défilement
   rapide, assez court pour être vue si l'on s'arrête sur la carte.
   Le compte est remis à zéro dès que la carte quitte le champ. */
(() => {
  const coarse = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!coarse) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const DELAY = 20000;
  const timers = new WeakMap();

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const card = entry.target;
      const video = card.querySelector('.pcard__video');
      if (!video) return;

      if (entry.isIntersecting) {
        if (timers.has(card)) return;
        timers.set(card, setTimeout(() => {
          card.classList.add('is-playing');
          const p = video.play();
          if (p && p.catch) p.catch(() => card.classList.remove('is-playing'));
        }, DELAY));
      } else {
        clearTimeout(timers.get(card));
        timers.delete(card);
        card.classList.remove('is-playing');
        video.pause();
        video.currentTime = 0;
      }
    });
  }, { threshold: 0.6 });

  const init = () => document.querySelectorAll('.pcard--video').forEach((c) => io.observe(c));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* REVELASHION — tiroirs panier et recherche.
   Les deux icônes restent de vrais liens : sans JavaScript, la recherche
   et le panier gardent leur page. On n'intercepte que pour ouvrir. */
(() => {
  const lock = (on) => { document.body.style.overflow = on ? 'hidden' : ''; };

  const openDrawer = (el) => {
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lock(true);
  };
  const closeDrawer = (el) => {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    lock(false);
  };
  const closeAll = () => document.querySelectorAll('.cd.is-open, .sd.is-open').forEach(closeDrawer);

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });

  /* ---------------------------------------------------------------
     Panier
     Le contenu est re-rendu par Shopify via l'API de rendu de sections
     plutôt que reconstruit en JavaScript : prix, devise et remises
     restent l'affaire des filtres Liquid.
     --------------------------------------------------------------- */
  const drawer = () => document.querySelector('[data-cart-drawer]');

  const refresh = async () => {
    const el = drawer();
    if (!el) return;
    try {
      const res = await fetch('/?sections=cart-drawer');
      const data = await res.json();
      const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const fresh = html.querySelector('[data-cart-drawer]');
      if (!fresh) return;
      const wasOpen = el.classList.contains('is-open');
      el.innerHTML = fresh.innerHTML;
      if (wasOpen) el.classList.add('is-open');
    } catch (e) { /* le lien vers /cart reste la porte de sortie */ }

    try {
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('[data-cart-count]').forEach((b) => {
        b.textContent = cart.item_count;
        b.hidden = cart.item_count === 0;
      });
    } catch (e) { /* la pastille garde sa valeur rendue au chargement */ }
  };

  const change = async (key, quantity) => {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    await refresh();
  };

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-cart-open]');
    if (open) { e.preventDefault(); openDrawer(drawer()); refresh(); return; }

    if (e.target.closest('[data-cart-close]')) { closeDrawer(drawer()); return; }

    const row = e.target.closest('.cd__item');
    if (!row) return;
    const key = row.dataset.key;
    const qty = Number(row.querySelector('.cd__qty span').textContent) || 1;

    if (e.target.closest('[data-cart-plus]')) change(key, qty + 1);
    else if (e.target.closest('[data-cart-minus]')) change(key, qty - 1);
    else if (e.target.closest('[data-cart-remove]')) change(key, 0);
  });

  /* Un ajout au panier ouvre le tiroir plutôt que de quitter la page :
     on reste là où on était. Le formulaire garde son action, donc sans
     script l'ajout fonctionne toujours, en rechargeant. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.action || !form.action.includes('/cart/add')) return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
      await refresh();
      openDrawer(drawer());
      /* Le panier a changé : ce qui en dépend ailleurs dans la page peut se
         remettre à jour, sans que ce gestionnaire ait à les connaître. */
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { form } }));
    } catch (err) {
      form.submit();
      return;
    }
    if (btn) btn.disabled = false;
  });

  /* ---------------------------------------------------------------
     Recherche
     --------------------------------------------------------------- */
  const sd = document.querySelector('[data-search-drawer]');
  if (!sd) return;
  const input = sd.querySelector('[data-search-input]');
  const results = sd.querySelector('[data-search-results]');

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      openDrawer(sd);
      /* Le focus attend la fin de l'ouverture : le donner à un champ
         encore masqué ferait sauter la page sur certains navigateurs. */
      setTimeout(() => input && input.focus(), 260);
      return;
    }
    if (e.target.closest('[data-search-close]')) closeDrawer(sd);
  });

  let timer = null;
  let last = '';
  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(timer);
    if (q.length < 2) { results.innerHTML = ''; last = ''; return; }
    /* On attend une pause de frappe : une requête par caractère
       saturerait l'API pour un résultat que personne ne lit. */
    timer = setTimeout(async () => {
      if (q === last) return;
      last = q;
      try {
        const url = '/search/suggest?q=' + encodeURIComponent(q)
          + '&resources[type]=product,page,article&resources[limit]=5'
          + '&section_id=predictive-search';
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('.shopify-section');
        results.innerHTML = inner ? inner.innerHTML : '';
      } catch (err) { results.innerHTML = ''; }
    }, 260);
  });
})();

/* ==================================================================
   REVEAL CLUB — animation du score
   Le chiffre et la barre sont déjà justes dans le HTML rendu par
   Shopify : le JavaScript ne fait que les mettre en scène. Sans lui,
   ou avec les animations réduites, on lit la valeur finale — jamais
   un zéro figé.
   ================================================================== */
(() => {
  const card = document.querySelector('[data-rc-card]');
  if (!card) return;

  /* La jauge a quitté la carte pour rejoindre le rail des paliers : on la
     cherche donc dans la section, pas dans la carte. C'est la carte qu'on
     observe malgré tout — la section fait plusieurs écrans de haut et un
     seuil de visibilité n'y serait jamais atteint. */
  const root = card.closest('.rcl') || document;
  const bar = root.querySelector('[data-rc-bar]');
  const count = card.querySelector('[data-rc-count]');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const fill = () => { if (bar) bar.classList.add('is-filled'); };

  const tally = () => {
    if (!count) return;
    const target = parseInt(count.getAttribute('data-rc-count'), 10);
    if (!Number.isFinite(target) || target <= 0) return;

    const DURATION = 1100;
    const start = performance.now();

    const step = (now) => {
      const t = Math.min((now - start) / DURATION, 1);
      /* easeOutCubic : le compteur ralentit en approchant du score,
         comme un cadran mécanique qui se cale. */
      const eased = 1 - Math.pow(1 - t, 3);
      count.textContent = Math.round(target * eased).toLocaleString('fr-FR');
      if (t < 1) requestAnimationFrame(step);
    };

    count.textContent = '0';
    requestAnimationFrame(step);
  };

  const run = () => { fill(); tally(); };

  if (reduce || !('IntersectionObserver' in window)) { fill(); return; }

  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      run();
      obs.disconnect();
    });
  }, { threshold: 0.4 });

  io.observe(card);
})();

/* ==================================================================
   REVEAL CLUB — bascule Reveal ID / Reveal Rewards
   Les onglets sont de vraies ancres : sans ce script les deux volets
   s'affichent l'un sous l'autre, ce qui reste lisible. Le script ne
   fait qu'en montrer un à la fois et éviter le saut de page.
   ================================================================== */
(() => {
  const tabs = document.querySelector('[data-rc-tabs]');
  if (!tabs) return;
  const page = tabs.closest('.rcx-page');
  if (!page) return;

  const buttons = [...tabs.querySelectorAll('[data-rc-tab]')];
  const panels = [...page.querySelectorAll('[data-rc-panel]')];
  if (!buttons.length || !panels.length) return;

  page.setAttribute('data-tabs-ready', '');

  const show = (name) => {
    buttons.forEach((b) => b.classList.toggle('is-on', b.dataset.rcTab === name));
    panels.forEach((p) => p.classList.toggle('is-on', p.dataset.rcPanel === name));
  };

  buttons.forEach((b) => {
    b.addEventListener('click', (e) => {
      e.preventDefault();
      show(b.dataset.rcTab);
      /* L'ancre reste dans l'URL pour que le volet survive au partage
         du lien et au rechargement, sans faire sauter la page. */
      history.replaceState(null, '', b.getAttribute('href'));
    });
  });

  /* Les raccourcis pointent vers des ancres logées dans l'un ou l'autre
     volet : on ouvre le bon avant de laisser le navigateur y aller. */
  page.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link || link.dataset.rcTab) return;
    const target = page.querySelector(link.getAttribute('href'));
    if (!target) return;
    const panel = target.closest('[data-rc-panel]');
    if (panel) show(panel.dataset.rcPanel);
  });

  const hash = window.location.hash.replace('#', '');
  if (hash === 'rc-rewards') show('rewards');
})();

/* ==================================================================
   PERSONNALISATION D'UN PACK — contrôle avant ajout au panier
   Le navigateur bloque déjà la soumission quand un choix obligatoire
   manque, mais sa bulle native est discrète et pointe une radio
   transparente. On affiche donc un message dans la section et on y
   amène la cliente. Le contrôle natif reste la ceinture ; ceci n'est
   que la sangle.
   ================================================================== */
(() => {
  const perso = document.querySelector('.perso');
  if (!perso) return;

  const groupes = () => [...perso.querySelectorAll('[data-perso-group]')];

  const verifier = () => {
    let premierManquant = null;
    groupes().forEach((grille) => {
      const set = grille.closest('.perso__set');
      /* Deux formes de choix cohabitent : la carte entière pour un modèle
         unique, une bulle par coloris pour un modèle décliné. Ne regarder
         que les premières faisait passer Wetlift pour non choisi alors
         qu'une couleur était bien cochée. */
      const radios = [...grille.querySelectorAll('.perso__radio, .perso__dot')];
      const requis = radios.some((r) => r.required);
      const choisi = radios.some((r) => r.checked);
      const manque = requis && !choisi;

      if (set) set.classList.toggle('is-missing', manque);
      const msg = set ? set.querySelector('[data-perso-error]') : null;
      if (msg) msg.hidden = !manque;
      if (manque && !premierManquant) premierManquant = set;
    });
    return premierManquant;
  };

  /* En capture : on veut trancher avant l'interception qui envoie le
     panier en arrière-plan, sinon l'ajout partirait sans les choix. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.action || !form.action.includes('/cart/add')) return;
    const manquant = verifier();
    if (!manquant) return;
    e.preventDefault();
    e.stopPropagation();
    manquant.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const premier = manquant.querySelector('.perso__radio, .perso__dot');
    if (premier) premier.focus({ preventScroll: true });
  }, true);

  /* Le message disparaît dès que le choix est fait. */
  perso.addEventListener('change', (e) => {
    if (!e.target.classList.contains('perso__radio') && !e.target.classList.contains('perso__dot')) return;
    const set = e.target.closest('.perso__set');
    if (!set) return;
    set.classList.remove('is-missing');
    const msg = set.querySelector('[data-perso-error]');
    if (msg) msg.hidden = true;
  });
})();

/* ==================================================================
   PERSONNALISATION — l'aperçu suit la couleur choisie
   Le choix reste porté par la radio ; ceci ne fait que montrer la
   bonne photo. Sans script, la carte garde l'image du produit et la
   sélection fonctionne quand même.
   ================================================================== */
(() => {
  const zone = document.querySelector('.perso');
  if (!zone) return;

  zone.addEventListener('change', (e) => {
    const dot = e.target.closest('.perso__dot');
    if (!dot) return;
    const carte = dot.closest('.perso__card--multi');
    const shot = carte ? carte.querySelector('.perso__shot') : null;
    const src = dot.dataset.persoShot;
    if (!shot || !src) return;
    /* `srcset` primerait sur `src` : on le retire, sinon l'image
       reviendrait à la version du produit au prochain redimensionnement. */
    shot.removeAttribute('srcset');
    shot.src = src;
  });
})();

/* REVELASHION — avant / après.
   Le geste est entièrement porté par l'`input range` du gabarit ; ce module
   ne fait que reporter sa valeur en variable CSS. Rien à écouter sur le
   pointeur, rien à gérer au clavier : le contrôle natif s'en charge. */
(() => {
  const bind = (frame) => {
    if (frame.dataset.baReady) return;
    const range = frame.querySelector('[data-ba-range]');
    if (!range) return;
    frame.dataset.baReady = '1';

    const pose = () => frame.style.setProperty('--pos', range.value + '%');
    range.addEventListener('input', pose);
    pose();
  };

  const init = () => document.querySelectorAll('[data-ba]').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();


/* REVELASHION — échange de points du Reveal Club.

   Le thème ne déduit rien : les points vivent dans un metafield client, et
   la vitrine ne peut pas y écrire. L'échange dépose la récompense au panier
   avec son coût inscrit sur la ligne ; c'est une règle Shopify Flow qui
   soustrait les points à la création de la commande.

   Après un échange, la liste est reconstruite par le serveur plutôt que
   corrigée à la main : accessibilité, solde engagé et état « déjà au
   panier » se déduisent tous du panier, et les recalculer ici reviendrait
   à tenir la même règle à deux endroits — donc à la voir diverger. */
(() => {
  const racine = () => document.querySelector('[data-rc-section]');

  const rafraichis = async () => {
    const sec = racine();
    const zone = sec && sec.querySelector('[data-rc-rewards]');
    if (!sec || !zone) return;

    try {
      const url = location.pathname + '?section_id=' + encodeURIComponent(sec.dataset.rcSection);
      const res = await fetch(url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fraiche = doc.querySelector('[data-rc-rewards]');
      if (!fraiche) return;

      /* Le contenu arrive avec ses accroches de révélation, que l'observateur
         d'origine n'observera jamais : sans cela, la liste rechargée resterait
         invisible. On la déclare déjà révélée. */
      fraiche.querySelectorAll('.rv').forEach((el) => el.classList.add('is-in', 'is-done'));
      zone.replaceWith(fraiche);
    } catch (e) {
      /* Le panier, lui, a bien été mis à jour : mieux vaut une liste en
         retard d'un rechargement qu'une erreur affichée à la cliente. */
    }
  };

  document.addEventListener('cart:added', (e) => {
    const form = e.detail && e.detail.form;
    if (form && form.hasAttribute('data-rc-redeem')) rafraichis();
  });
})();
